/**
 * 로그인 후 이동할 주소로 쓸 수 있는 값인지 판별한다.
 *
 * `redirectUrl` 은 `/login?redirectUrl=...` 로 들어오는 **사용자 제어 값**이고,
 * 최종적으로 `router.push()` / `router.replace()` 에 그대로 들어간다
 * (`src/components/toast/index.tsx`). 절대 URL 을 그대로 통과시키면
 * `/login?redirectUrl=https://evil.example` 로 로그인 직후 외부 사이트로 튕기는
 * 오픈 리다이렉트가 된다.
 *
 * 같은 출처의 경로만 허용한다:
 * - `/` 로 시작해야 한다 (절대 URL·스킴 상대 URL 차단)
 * - `//host` `/\\host` 는 브라우저가 스킴 상대 URL 로 해석하므로 차단
 * - 제어문자(개행·탭 등)는 파서 우회에 쓰이므로 차단
 */
export const toSafeRedirectPath = (value: string | string[] | undefined): string | undefined => {
  const candidate = Array.isArray(value) ? value[0] : value;
  if (typeof candidate !== 'string' || candidate === '') return undefined;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(candidate)) return undefined;
  if (!candidate.startsWith('/')) return undefined;
  if (candidate.startsWith('//') || candidate.startsWith('/\\')) return undefined;
  return candidate;
};

export type ToastRedirectTarget = { url: string; option?: 'REPLACE' | 'PUSH' };
export type ResolvedToastRedirect = { url: string; option: 'REPLACE' | 'PUSH' };

/**
 * 토스트가 닫힌 뒤 어디로 이동할지 결정한다. 앱에서 그 이동을 실제로 수행하는 곳은
 * `components/toast` 한 곳뿐이므로, **여기가 리다이렉트 목적지가 정해지는 유일한 지점**이다.
 *
 * 이 url 은 로그인 흐름에서 `?redirectUrl=` 을 타고 들어오는 사용자 제어 값일 수 있다.
 * 같은 출처 경로가 아니면 `null` 을 돌려 **이동 자체를 하지 않는다**
 * (외부로 튕기느니 그대로 머무는 편이 낫다).
 *
 * 컴포넌트에서 분리해 둔 이유는 이 판단을 렌더러 없이 그대로 실행해 검증하기 위해서다.
 */
export const resolveToastRedirect = (target: ToastRedirectTarget | undefined): ResolvedToastRedirect | null => {
  if (!target || target.url === '') return null;
  const url = toSafeRedirectPath(target.url);
  if (!url) return null;
  return { url, option: target.option ?? 'PUSH' };
};
